import { createTheme } from '@mui/material/styles';

/**
 * English Teacher Admin — central design tokens.
 *
 * Mirror of the Website theme so admin + portal + public site share
 * the same brand tokens. See English-Teacher-Website/src/theme.js for
 * the source of truth. Kept as a separate file (rather than a shared
 * package) because the two repos deploy independently.
 */

const COLORS = {
    primary: '#6D3FC5',
    primaryDark: '#5A2FA6',
    primaryLight: '#9066D8',
    primaryLighter: '#EFE7FB',
    secondary: '#FF0896',
    secondaryDark: '#D0067A',
    secondaryLight: '#FF5CBC',
    ctaGold: '#FFB600',
    ctaGoldLight: '#FFD24D',
    brandGradientStops: [
        '#f27b81',
        '#f7c44e',
        '#aec158',
        '#b0b6fb',
        '#dd72b3',
        '#fcc6e6',
    ],
    text: {
        primary: '#101828',
        secondary: '#475467',
        muted: '#6B7280',
        onDark: '#FFFFFF',
    },
    surface: {
        page: '#F9FAFB',
        card: '#FFFFFF',
        border: '#E5E7EB',
    },
    success: '#16A34A',
    warning: '#F59E0B',
    error: '#DC2626',
    info: '#0EA5E9',
};

const BRAND_GRADIENT = `linear-gradient(90deg, ${COLORS.brandGradientStops.join(', ')})`;
const CTA_GRADIENT = `linear-gradient(180deg, ${COLORS.ctaGold} 0%, ${COLORS.ctaGoldLight} 100%)`;

const BUTTON_RADIUS = 10;

const theme = createTheme({
    palette: {
        primary: {
            main: COLORS.primary,
            dark: COLORS.primaryDark,
            light: COLORS.primaryLight,
            lighter: COLORS.primaryLighter,
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: COLORS.secondary,
            dark: COLORS.secondaryDark,
            light: COLORS.secondaryLight,
            contrastText: '#FFFFFF',
        },
        text: {
            primary: COLORS.text.primary,
            secondary: COLORS.text.secondary,
        },
        background: {
            default: COLORS.surface.page,
            paper: COLORS.surface.card,
        },
        success: { main: COLORS.success },
        warning: { main: COLORS.warning },
        error: { main: COLORS.error },
        info: { main: COLORS.info },
        divider: COLORS.surface.border,
    },
    brand: {
        gradient: BRAND_GRADIENT,
        gradientStops: COLORS.brandGradientStops,
        ctaGold: COLORS.ctaGold,
        ctaGradient: CTA_GRADIENT,
        tertiaryBorder: '#101828',
    },
    typography: {
        fontFamily:
            '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
        h1: { fontFamily: '"Inter", sans-serif', fontWeight: 700 },
        h2: { fontFamily: '"Inter", sans-serif', fontWeight: 700 },
        h3: { fontFamily: '"Inter", sans-serif', fontWeight: 700 },
        h4: { fontFamily: '"Inter", sans-serif', fontWeight: 700 },
        h5: { fontFamily: '"Inter", sans-serif', fontWeight: 700 },
        h6: { fontFamily: '"Inter", sans-serif', fontWeight: 700 },
        button: { fontFamily: '"Inter", sans-serif', fontWeight: 600, textTransform: 'none' },
    },
    shape: {
        borderRadius: 8,
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                ':root': {
                    '--et-primary': COLORS.primary,
                    '--et-primary-dark': COLORS.primaryDark,
                    '--et-primary-light': COLORS.primaryLight,
                    '--et-primary-lighter': COLORS.primaryLighter,
                    '--et-secondary': COLORS.secondary,
                    '--et-secondary-dark': COLORS.secondaryDark,
                    '--et-secondary-light': COLORS.secondaryLight,
                    '--et-cta-gold': COLORS.ctaGold,
                    '--et-cta-gradient': CTA_GRADIENT,
                    '--et-brand-gradient': BRAND_GRADIENT,
                    '--et-text-primary': COLORS.text.primary,
                    '--et-text-secondary': COLORS.text.secondary,
                    '--et-surface-page': COLORS.surface.page,
                    '--et-surface-card': COLORS.surface.card,
                    '--et-border': COLORS.surface.border,
                    '--et-button-radius': `${BUTTON_RADIUS}px`,
                    '--et-font-family':
                        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                },
                body: {
                    fontFamily:
                        '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                    color: COLORS.text.primary,
                    // Admin app has its own greyed page background pattern;
                    // keep the neutral rather than tinting.
                    backgroundColor: '#F5F5F5',
                },
            },
        },
        MuiButton: {
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: {
                    borderRadius: BUTTON_RADIUS,
                    textTransform: 'none',
                    fontWeight: 600,
                    fontFamily: '"Inter", sans-serif',
                },
            },
            variants: [
                // Force the built-in MUI `variant="outlined"` (which
                // would otherwise render as purple-outlined because
                // primary is purple) to look exactly like tertiary
                // (black outline, black text). This catches every
                // legacy call site that used <Button variant="outlined">
                // without asking for a brand mismatch.
                {
                    props: { variant: 'outlined' },
                    style: {
                        backgroundColor: '#FFFFFF',
                        color: COLORS.text.primary,
                        border: `1.5px solid ${COLORS.text.primary}`,
                        '&:hover': {
                            backgroundColor: '#F3F4F6',
                            borderColor: COLORS.text.primary,
                        },
                    },
                },
                // The !important on the label color guards against
                // ad-hoc inline `color` props or old `sx={{ color: … }}`
                // overrides on call sites — the filled brand fills
                // MUST render label text in white to stay legible.
                {
                    props: { variant: 'primary' },
                    style: {
                        backgroundColor: COLORS.primary,
                        color: '#FFFFFF !important',
                        '&:hover': { backgroundColor: COLORS.primaryDark },
                        '&:disabled': { backgroundColor: '#C7B8E4', color: '#FFFFFF !important' },
                    },
                },
                {
                    props: { variant: 'secondary' },
                    style: {
                        backgroundColor: COLORS.secondary,
                        color: '#FFFFFF !important',
                        '&:hover': { backgroundColor: COLORS.secondaryDark },
                        '&:disabled': { backgroundColor: '#F7B5D6', color: '#FFFFFF !important' },
                    },
                },
                {
                    props: { variant: 'tertiary' },
                    style: {
                        backgroundColor: '#FFFFFF',
                        color: `${COLORS.text.primary} !important`,
                        border: `1.5px solid ${COLORS.text.primary}`,
                        '&:hover': {
                            backgroundColor: '#F3F4F6',
                            borderColor: COLORS.text.primary,
                        },
                    },
                },
                {
                    props: { variant: 'cta' },
                    style: {
                        background: CTA_GRADIENT,
                        color: COLORS.primary,
                        fontWeight: 700,
                        '&:hover': {
                            background: `linear-gradient(180deg, ${COLORS.ctaGoldLight} 0%, ${COLORS.ctaGold} 100%)`,
                        },
                    },
                },
            ],
        },
        MuiLink: {
            defaultProps: { underline: 'hover' },
            styleOverrides: { root: { color: COLORS.primary } },
        },
        MuiChip: {
            styleOverrides: {
                root: { fontFamily: '"Inter", sans-serif', fontWeight: 500 },
                // Force white label text on the filled colour variants
                // (info / success / warning / error / primary / secondary).
                // Default MUI contrastText picks dark text on info-blue,
                // which reads as illegible on the Tutoring chip in the
                // enquiries table.
                filledInfo: { color: '#FFFFFF' },
                filledSuccess: { color: '#FFFFFF' },
                filledWarning: { color: '#FFFFFF' },
                filledError: { color: '#FFFFFF' },
                filledPrimary: { color: '#FFFFFF' },
                filledSecondary: { color: '#FFFFFF' },
            },
        },
    },
});

export default theme;
export { COLORS, BRAND_GRADIENT, CTA_GRADIENT, BUTTON_RADIUS };
